import { toArray, toString } from 'lodash-es';
import type {
  AppVersionApplicationFile,
  AppVersionExistingAppFiles,
  ParameterTemplate,
} from '../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';

import type { TFunction } from 'i18next';
import { getHumanizedFileSize } from '../../utils/fileSizeConversion.util';
import type { DeviceAppVersionFilesListItem } from '../device-app-version-files-list/deviceAppVersionFilesList.model';
import type { SystemPackageVersionAutocompleteFieldValue } from '../system-package-version-fields/systemPackageVersionFields.model';
import type { BuildTemplateInfoParams } from './systemPackageVersionSummaryStep.model';

export function getSelectetInputDisplyedValuesInline(
  selectValues?: SystemPackageVersionAutocompleteFieldValue[],
): string {
  return toString(selectValues?.map(({ label }) => label).join(', '));
}

export const templateInfoJoiner = ' • ';

export function buildTemplateInfo({
  targetLocation,
  targetLocationFieldLabel,
  version,
  versionFieldLabel,
  id,
  name,
  nameFieldLabel,
  idFieldLabel,
  idFieldSize,
  size,
  t,
}: BuildTemplateInfoParams): string {
  return [
    nameFieldLabel && name ? `${nameFieldLabel}: ${name}` : undefined,
    id ? `${idFieldLabel}: ${id}` : undefined,
    version && versionFieldLabel ? `${versionFieldLabel}: ${version}` : undefined,
    targetLocation ? `${targetLocationFieldLabel}: ${targetLocation}` : undefined,
    size ? `${idFieldSize}: ${getHumanizedFileSize(size, t)}` : undefined,
  ]
    .filter(Boolean)
    .join(templateInfoJoiner);
}

export function getTemplatesListForSummary({
  parameterTemplate: { template, targetLocation },
  targetLocationFieldLabel,
  versionFieldLabel,
  nameFieldLabel,
  idFieldLabel,
  idFieldSize,
  t,
}: {
  readonly parameterTemplate: ParameterTemplate;
  readonly targetLocationFieldLabel: string;
  readonly versionFieldLabel: string;
  readonly nameFieldLabel: string;
  readonly idFieldLabel: string;
  readonly idFieldSize: string;
  readonly t: TFunction;
}): DeviceAppVersionFilesListItem[] {
  return template.map(({ name, size, parsedTemplateId, downloadPolicy }) => ({
    filename: toString(parsedTemplateId ?? name),
    fileInfo: buildTemplateInfo({
      size,
      targetLocation,
      targetLocationFieldLabel,
      parsedTemplateId,
      versionFieldLabel,
      nameFieldLabel,
      idFieldLabel,
      name,
      idFieldSize,
      t,
    }),
    unistalledApps: downloadPolicy?.map(({ name }) => name),
  }));
}

export function buildDeviceAppVersionFilesListItemFromTemplates({
  templates,
  targetLocationFieldLabel,
  versionFieldLabel,
  nameFieldLabel,
  idFieldLabel,
  idFieldSize,
  t,
}: {
  readonly templates: ParameterTemplate[];
  readonly targetLocationFieldLabel: string;
  readonly versionFieldLabel: string;
  readonly nameFieldLabel: string;
  readonly idFieldLabel: string;
  readonly idFieldSize: string;
  readonly t: TFunction;
}): DeviceAppVersionFilesListItem[] {
  return toArray(
    templates?.reduce(
      (accum, current) => [
        ...accum,
        ...getTemplatesListForSummary({
          parameterTemplate: current,
          targetLocationFieldLabel,
          versionFieldLabel,
          nameFieldLabel,
          idFieldLabel,
          idFieldSize,
          t,
        }),
      ],
      [] as DeviceAppVersionFilesListItem[],
    ),
  );
}

export function convertExistingTemplatesToFilesListItem({
  existingTemplatesFormValues,
  existingTemplates,
  targetLocationFieldLabel,
  versionFieldLabel,
  nameFieldLabel,
  idFieldLabel,
  idFieldSize,
  t,
}: {
  readonly existingTemplatesFormValues: AppVersionExistingAppFiles[];
  readonly existingTemplates: AppVersionApplicationFile[];
  readonly targetLocationFieldLabel: string;
  readonly versionFieldLabel: string;
  readonly nameFieldLabel: string;
  readonly idFieldLabel: string;
  readonly idFieldSize: string;
  readonly t: TFunction;
}): DeviceAppVersionFilesListItem[] {
  const existingTemplatesObj = toArray(existingTemplates).reduce(
    (accum, current) => ({
      ...accum,
      [toString(current.id ?? current.name)]: {
        name: current.name,
        file: current.file,
        size: current.size,
        targetLocation: current?.targetLocation,
        id: current?.id,
        parsedTemplateId: current?.parsedTemplateId,
        templateFileVersion: current?.templateFileVersion,
        templateFileName: current?.templateFileName,
      },
    }),
    {} as Record<
      string,
      {
        name: string;
        file?: File;
        size: number;
        targetLocation?: string;
        id?: string;
        parsedTemplateId?: string;
        templateFileVersion?: string;
        templateFileName?: string;
      }
    >,
  );

  return toArray(
    existingTemplatesFormValues?.map(({ value, downloadPolicy }) => ({
      filename: toString(existingTemplatesObj[value]?.name),
      fileInfo: existingTemplatesObj[value]?.targetLocation
        ? buildTemplateInfo({
            size: existingTemplatesObj[value]?.size ?? 0,
            targetLocation: toString(existingTemplatesObj[value]?.targetLocation),
            targetLocationFieldLabel,
            id: existingTemplatesObj[value]?.id,
            parsedTemplateId: existingTemplatesObj[value]?.parsedTemplateId,
            version: existingTemplatesObj[value]?.templateFileVersion,
            versionFieldLabel,
            name: existingTemplatesObj[value]?.templateFileName,
            nameFieldLabel,
            idFieldLabel,
            idFieldSize,
            t,
          })
        : getHumanizedFileSize(existingTemplatesObj[value]?.size ?? 0, t),
      unistalledApps: downloadPolicy?.map(({ name }) => name),
    })),
  );
}

export function getDisplayedAutocompleteFieldValue(
  options?: SystemPackageVersionAutocompleteFieldValue[],
  selected?: string,
): string {
  const found = options?.find(({ value }) => value === selected);
  return toString(found?.label);
}
